import { useCallback, useEffect, useReducer, useRef } from "react";
import { calculateAvailablePages } from "~/helpers/paginationHelper";
import APIRequest from "~/lib/api_request";
import {
  ACTIONS,
  initialPaginatedState,
  paginatedDataReducer,
} from "./usePaginatedData.reducer";

const DEFAULT_MAX_ITEMS = 1000;

/**
 * @typedef {'page' | 'cursor'} PaginationStyle
 *
 * `page` — numeric page numbers; consumer + server agree on integer
 * keys, the hook tracks loaded-page de-duplication and reconstructs
 * neighbouring pages via {@link calculateAvailablePages}. Backward-compatible default.
 *
 * `cursor` — opaque cursor strings (e.g. Pagy keyset); the hook
 * trusts the backend's `nextPage`/`prevPage` verbatim, skips page-number
 * arithmetic, and preserves the loaded-cursor de-duplication Set so
 * accidental re-fetches of the same cursor are still skipped.
 */

const usePaginatedData = ({
  fetchFn,
  resourceId,
  dependencies = [],
  anchorId = null,
  parseResponse,
  buildRequestData,
  maxItems = DEFAULT_MAX_ITEMS,
  shouldFetch = true,
  autoFetch = true, // When false, consumer controls fetching (for debounce scenarios)
  onExtraData,
  paginationStyle = "page",
}) => {
  const [state, dispatch] = useReducer(
    paginatedDataReducer,
    initialPaginatedState,
  );

  const currentRequest = useRef(null);
  const currentResourceId = useRef(resourceId);
  const isMounted = useRef(true);

  const loadedPagesRef = useRef(new Set());
  const knownLastPage = useRef(null);
  const initialAnchorRef = useRef(anchorId);

  const currentFetchDirection = useRef("next");
  const prevItemsLengthRef = useRef(0);
  const isFetchingRef = useRef(false);

  const fetchSerial = useRef(0);

  const fetchData = useCallback(
    (reset = false, useAnchor = false) => {
      if (!shouldFetch) {
        return;
      }

      // Skip non-reset fetches while already fetching (prevents duplicate requests)
      // Use ref to get current fetching state (avoids stale closure)
      if (isFetchingRef.current && !reset) {
        return;
      }

      const pageToFetch = reset ? 1 : state.page;

      if (!reset && !useAnchor && loadedPagesRef.current.has(pageToFetch)) {
        return;
      }

      if (currentRequest.current) {
        APIRequest.abort(currentRequest.current);
      }

      isFetchingRef.current = true;

      dispatch({
        type: ACTIONS.FETCH_START,
        reset,
        direction: currentFetchDirection.current,
      });

      if (reset) {
        loadedPagesRef.current.clear();
        knownLastPage.current = null;
      }

      currentResourceId.current = resourceId;
      const thisFetchSerial = fetchSerial.current;

      const shouldUseAnchor =
        useAnchor &&
        initialAnchorRef.current &&
        (reset || !state.hasLoadedAnchor);
      const requestData = buildRequestData(
        pageToFetch,
        shouldUseAnchor,
        initialAnchorRef.current,
      );

      currentRequest.current = fetchFn(requestData).end((err, response) => {
        // Guard: component unmounted
        if (!isMounted.current) {
          return;
        }

        // Guard: stale response (resourceId or dependencies changed)
        if (
          currentResourceId.current !== resourceId ||
          thisFetchSerial !== fetchSerial.current
        ) {
          return;
        }

        isFetchingRef.current = false;
        dispatch({ type: ACTIONS.SET_LOADING, payload: false });
        currentRequest.current = null;

        if (err && err.code !== "ABORTED") {
          dispatch({ type: ACTIONS.FETCH_ERROR, payload: err });
          return;
        }
        if (err) {
          return; // ABORTED - loading already cleared above
        }

        const { items, pagination, extra } = parseResponse(response);

        const fetchedPageNumber = pagination?.page || pageToFetch;
        loadedPagesRef.current.add(fetchedPageNumber);

        if (!pagination?.nextPage) {
          knownLastPage.current = fetchedPageNumber;
        }

        const { actualPrevPage, actualNextPage } =
          paginationStyle === "cursor"
            ? {
                actualPrevPage: pagination?.prevPage ?? null,
                actualNextPage: pagination?.nextPage ?? null,
              }
            : calculateAvailablePages(
                pagination?.nextPage,
                pagination?.prevPage,
                state.nextPage,
                loadedPagesRef.current,
                knownLastPage.current,
              );

        // Handle anchor page setting
        let anchorLoaded = false;
        if (shouldUseAnchor) {
          anchorLoaded = true;
          if (pagination?.page && pagination.page !== state.page) {
            dispatch({
              type: ACTIONS.SET_PAGE_FROM_ANCHOR,
              payload: pagination.page,
            });
          }
        }

        // Update state with fetched data
        dispatch({
          type: ACTIONS.FETCH_SUCCESS,
          payload: {
            items,
            pagination: {
              nextPage: actualNextPage,
              prevPage: actualPrevPage,
            },
            maxItems,
            reset,
            direction: currentFetchDirection.current,
          },
          anchorLoaded,
        });

        // Call extra data handler
        if (extra && onExtraData) {
          onExtraData(extra);
        }
      });
    },
    [
      shouldFetch,
      state.page,
      state.hasLoadedAnchor,
      state.nextPage,
      resourceId,
      buildRequestData,
      fetchFn,
      parseResponse,
      maxItems,
      onExtraData,
      paginationStyle,
    ],
  );

  const loadMore = useCallback(
    (direction = "next") => {
      if (direction === "init") {
        currentFetchDirection.current = "next";
        fetchData(true, false);
        return;
      }
      if (state.loading) {
        return;
      }

      if (direction === "next" && state.nextPage) {
        currentFetchDirection.current = "next";
        dispatch({ type: ACTIONS.SET_PAGE, payload: state.nextPage });
      } else if (direction === "prev" && state.prevPage) {
        prevItemsLengthRef.current = state.items.length;
        currentFetchDirection.current = "prev";
        dispatch({ type: ACTIONS.SET_PAGE, payload: state.prevPage });
      }
    },
    [
      state.loading,
      state.nextPage,
      state.prevPage,
      state.items.length,
      fetchData,
    ],
  );

  const setItems = useCallback(
    (items) => {
      if (typeof items === "function") {
        dispatch({ type: ACTIONS.SET_ITEMS, payload: items(state.items) });
      } else {
        dispatch({ type: ACTIONS.SET_ITEMS, payload: items });
      }
    },
    [state.items],
  );

  const setLoading = useCallback((loading) => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: loading });
  }, []);

  const setPage = useCallback((page) => {
    dispatch({ type: ACTIONS.SET_PAGE, payload: page });
  }, []);

  const resetPagination = useCallback(() => {
    loadedPagesRef.current.clear();
    knownLastPage.current = null;
    dispatch({ type: ACTIONS.RESET });
  }, []);

  // Allows consumer to invalidate in-flight requests when their own state changes
  // (e.g., useConversations filter changes that aren't in usePaginatedData dependencies)
  const invalidateStaleResponses = useCallback(() => {
    fetchSerial.current += 1;
  }, []);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Main effect: fetch on resourceId or dependencies change
  useEffect(() => {
    // Increment serial to invalidate any in-flight requests
    fetchSerial.current += 1;

    // Reset pagination tracking
    loadedPagesRef.current.clear();
    knownLastPage.current = null;
    initialAnchorRef.current = anchorId;
    currentFetchDirection.current = "next";
    prevItemsLengthRef.current = 0;
    isFetchingRef.current = false;

    dispatch({ type: ACTIONS.RESET });

    // When autoFetch is false, consumer controls fetching (e.g., for debounce)
    if (autoFetch) {
      const useAnchor = !!anchorId;
      fetchData(true, useAnchor);
    }

    return () => {
      if (currentRequest.current) {
        APIRequest.abort(currentRequest.current);
      }
    };
    // fetchData intentionally excluded to prevent infinite loop.
    // This effect should only run when resourceId or dependencies change.
  }, [resourceId, autoFetch, ...dependencies]);

  // Page change effect
  useEffect(() => {
    if (state.isSettingPageFromAnchor) {
      dispatch({ type: ACTIONS.CLEAR_ANCHOR_FLAG });
      return;
    }

    // Only fetch if not initial load (page 1 with no loaded pages is handled by main effect)
    if (state.page !== 1 || loadedPagesRef.current.size > 0) {
      fetchData();
    }
    // fetchData intentionally excluded - see comment above.
  }, [state.page, state.isSettingPageFromAnchor]);

  return {
    // State
    items: state.items,
    loading: state.loading,
    loadingDirection: state.loadingDirection,
    loadingNext: state.loading && state.loadingDirection !== "prev",
    loadingPrev: state.loading && state.loadingDirection === "prev",
    error: state.error,
    nextPage: state.nextPage,
    prevPage: state.prevPage,
    prevItemsLength: prevItemsLengthRef.current,

    // Setters
    setItems,
    setLoading,
    setPage,

    // Actions
    fetch: fetchData,
    loadMore,
    resetPagination,
    invalidateStaleResponses,
  };
};

export default usePaginatedData;
